import {
  useEffect, useRef,
} from 'react';
import {
  areSetsEqual, flatten,
} from '~/_shared/utils/collections/collections';
import { useWaitForDataReady } from '~/map/afterMigrationMapPrepareModal/useWaitForDataReady';
import {
  getSingleZIndex, ZIndexedEntity,
} from '~/map/zIndexes/zIndexRanges';
import { useBoundaryIdsFromBoundaryFilter } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryIdsFromBoundaryFilter';
import { useZoomToBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useZoomToBoundary';
import {
  type BoundaryGroupId, type BoundaryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryLocationsFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { useTheme } from '../../../../_shared/themes/theme.hooks';
import { useMapSettingsBoundariesIsolateColor } from '../../../../store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { type MapBoundaryManager } from '../mapBoundary.manager';
import { getIsolateBoundaryColorHex } from './mapIsolateBoundaries.helper';

export const useMapIsolateBoundaries = (manager: MapBoundaryManager | undefined) => {
  const theme = useTheme();
  const boundaryLocationsFilters = useBoundaryLocationsFilters();
  const coverPolygonZIndex = getSingleZIndex(ZIndexedEntity.CoverPolygon);
  const { zoomToBoundary } = useZoomToBoundary();
  const isolateColor = useMapSettingsBoundariesIsolateColor();
  const boundariesMap = useBoundaryIdsFromBoundaryFilter(boundaryLocationsFilters.isolated);
  const previousIsolatedBoundaryIdsRef = useRef<ReadonlyMap<BoundaryGroupId, ReadonlySet<BoundaryId>>>(new Map());
  const isMapReady = useWaitForDataReady();

  useEffect(() => {
    if (!manager) {
      return;
    }

    manager.updateIsolateColor(getIsolateBoundaryColorHex(theme, isolateColor));
  }, [manager, isolateColor, theme]);

  useEffect(() => {
    if (!manager) {
      return;
    }

    const boundariesMapEntries = Array.from(boundariesMap.entries());

    const isTheSame = boundariesMapEntries.every(([bgId, boundaries]) => areSetsEqual(previousIsolatedBoundaryIdsRef.current.get(bgId) ?? new Set(), boundaries));
    if (!isTheSame) {
      if (isMapReady) { // if map is not ready we are still resolving initial zoom so we don't want this to clash with that
        zoomToBoundary(flatten(boundariesMapEntries.map(([boundaryGroupId, boundaryIds]) => (
          Array.from(boundaryIds.values()).map(boundaryId => ({ boundaryGroupId, boundaryId }))
        ))));
      }
    }

    manager.updateIsolateBoundaries(boundariesMap, coverPolygonZIndex);
    previousIsolatedBoundaryIdsRef.current = boundariesMap;
  }, [boundariesMap, coverPolygonZIndex, manager, zoomToBoundary, isMapReady]);

  useEffect(() => () => {
    if (manager) {
      manager.updateIsolateBoundaries(new Map());
    }
  }, [manager]);
};
